import React, { useEffect, useRef, useState } from 'react'
import { BarcodeScannerDemo } from '../../components/BarcodeScannerDemo'
import Modal from '../../components/Modal'
import { Button } from '../../components/shadcn/Button'
import { getSetting } from '../../contexts/SettingsContext'
import { buildCameraConstraints } from '../../lib/Camera'

export type UnstructuredSettingsFlowId =
  | 'printer1'
  | 'printer2'
  | 'printer2byBrowser'
  | 'scannerTest'
  | 'camera1'
  | 'cameraTest'

// Function that implements all the stange settings that are not just 'store in memory and use in components' ones
// fr i am too afraid to touch all antient printer settings, sooo i will just use localstorage keys as before
// cause i do not know if core uses them or not, and i am too lazy to re-implement this lol.
export const useUnstructuredSettingsFlow = () => {
  const [isScannerDemoOpen, setIsScannerDemoOpen] = useState<boolean>(false)
  const [isCameraTestOpen, setIsCameraTestOpen] = useState<boolean>(false)
  const [cameraTestImage, setCameraTestImage] = useState<string | null>(null)
  const cameraVideoRef = useRef<HTMLVideoElement | null>(null)
  const cameraStreamRef = useRef<MediaStream | null>(null)
  const selectPrinter = (printerName: string) => {
    if (printerName === '') {
      window.localStorage.removeItem('printer')
    } else {
      window.localStorage.setItem('printer', printerName)
    }
  }

  const selectPrinter2 = (printerName: string) => {
    if (printerName === '') {
      window.localStorage.removeItem('printer2')
    } else {
      window.localStorage.setItem('printer2', printerName)
    }
  }

  const selectPrinter2ByBrowser = (isChecked: boolean) => {
    if (!isChecked) {
      window.localStorage.removeItem('printer2byBrowser')
    } else {
      window.localStorage.setItem('printer2byBrowser', 'true')
    }
  }

  // Camera test preview
  useEffect(() => {
    if (!isCameraTestOpen) {
      if (cameraStreamRef.current) {
        cameraStreamRef.current.getTracks().forEach(track => track.stop())
        cameraStreamRef.current = null
      }
      return
    }

    let cancelled = false

    if (!navigator.mediaDevices?.getUserMedia) {
      console.error('Camera API is not available in this browser.')
      return
    }

    const preferredId = (getSetting('cameraDeviceId') as string | null) || ''
    const constraints = buildCameraConstraints(preferredId)

    navigator.mediaDevices
      .getUserMedia(constraints)
      .then(stream => {
        if (cancelled) {
          stream.getTracks().forEach(track => track.stop())
          return
        }

        cameraStreamRef.current = stream

        if (cameraVideoRef.current) {
          cameraVideoRef.current.srcObject = stream
          cameraVideoRef.current.play().catch(error => console.error('Error playing camera stream:', error))
        }
      })
      .catch(error => {
        console.error('Error accessing camera for settings preview:', error)
      })

    return () => {
      cancelled = true
      if (cameraStreamRef.current) {
        cameraStreamRef.current.getTracks().forEach(track => track.stop())
        cameraStreamRef.current = null
      }
    }
  }, [isCameraTestOpen])

  const handleCameraTestCapture = () => {
    const video = cameraVideoRef.current
    if (!video) return

    const width = video.videoWidth || 1280
    const height = video.videoHeight || 720

    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    ctx.drawImage(video, 0, 0, width, height)
    const dataUrl = canvas.toDataURL('image/jpeg')
    setCameraTestImage(dataUrl)
  }

  const closeCameraTest = () => {
    setIsCameraTestOpen(false)
    setCameraTestImage(null)
  }

  return {
    handleToggle: (id: UnstructuredSettingsFlowId, value: boolean) => {
      if (id === 'printer2byBrowser') {
        selectPrinter2ByBrowser(value)
      }
    },
    handlePicker: (id: UnstructuredSettingsFlowId, value: string) => {
      if (id === 'printer1') {
        selectPrinter(value)
      } else if (id === 'printer2') {
        selectPrinter2(value)
      }
    },
    getPickerItems: async (id: UnstructuredSettingsFlowId) => {
      if (id.includes('printer')) {
        const desktopApp = (window as any)._depoto as any

        const printers = await Promise.all([
          desktopApp?.node?.getPrinters(),
          new Promise(resolve => {
            desktopApp?.nw?.win?.getPrinters((p: any[]) => resolve(p.map((d: any) => d.deviceName)))
          }),
        ])

        const flatPrinters = printers.flat().filter((p: string) => p !== 'or' && typeof p === 'string')
        const printersList = [...new Set(flatPrinters)]

        if (printersList.length > 0) {
          return printersList.map(p => ({ label: p, value: p }))
        }

        return []
      }

      if (id === 'camera1') {
        if (!navigator.mediaDevices?.enumerateDevices) {
          return []
        }
        try {
          const devices = await navigator.mediaDevices.enumerateDevices()
          const cameras = devices.filter(d => d.kind === 'videoinput')

          if (!cameras.length) {
            // žádná videovstupní zařízení -> necháme picker prázdný
            return []
          }

          // Použijeme jen kamery, které mají nějaké skutečné deviceId
          const usableCameras = cameras.filter(cam => cam.deviceId && cam.deviceId.length > 0)

          if (!usableCameras.length) {
            // Browser hlásí "něco", ale bez deviceId – raději to skryjeme,
            // než abychom ukazovali falešné "Kamera 1"
            return []
          }

          return usableCameras.map((cam, index) => ({
            label: cam.label || `Kamera ${index + 1}`,
            value: cam.deviceId!,
          }))
        } catch (e) {
          console.warn('Failed to enumerate cameras', e)
          return []
        }
      }

      return []
    },
    handleButton: (id: UnstructuredSettingsFlowId) => {
      if (id === 'scannerTest') {
        setIsScannerDemoOpen(true)
      }
      if (id === 'cameraTest') {
        setIsCameraTestOpen(true)
      }
    },
    renderModals: () => {
      return (
        <>
          <BarcodeScannerDemo isOpen={isScannerDemoOpen} onClose={() => setIsScannerDemoOpen(false)} />
          <Modal isOpen={isCameraTestOpen} onClose={closeCameraTest} title="Test kamery">
            <div className="w-full space-y-4">
              <div className="aspect-video bg-black rounded-md overflow-hidden flex items-center justify-center">
                <video ref={cameraVideoRef} className="w-full h-full object-cover" autoPlay playsInline muted />
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={handleCameraTestCapture} icon={['far', 'camera']}>
                  Pořídit testovací fotku
                </Button>
                <Button variant="default" onClick={closeCameraTest} icon={['far', 'xmark']}>
                  Zavřít
                </Button>
              </div>
              {cameraTestImage && (
                <div className="space-y-2">
                  <div className="text-sm text-gray-600">Naposledy pořízená testovací fotka:</div>
                  <img src={cameraTestImage} alt="Náhled testovací fotky" className="w-full rounded-md border" />
                </div>
              )}
            </div>
          </Modal>
        </>
      )
    },
  }
}
